import { MapboxGlContainer } from "@/features/mapbox-gl-container";
import useGetBranch from "@/shared/hooks/useGetBranch";
import { useSteps } from "@/shared/hooks/useGetSteps";
import { DefaultStepValues } from "@/shared/lib/step.constants";
import { Button, Card, Col, Divider, Flex, Row, Typography } from "antd";
import dayjs from "dayjs";
import { type FC } from "react";
import { WEEKDAY_LABEL } from "../../lib/time.constants";
import { sortWeekDays } from "../../lib/time.utils";
import useMainScreenStyles from "./MainScreen.styles";

const TEXT = {
  contacts: {
    phone: "Номер телефона",
    phoneAlt: "Дополнительный номер телефона",
    email: "Почтовый адрес",
  },
  sections: {
    address: "Адрес",
    schedule: "Расписание",
    contacts: "Контакты",
  },
  schedule: {
    weekend: "Выходной",
  },
} as const;

interface MainScreenProps {}

const MainScreen: FC<MainScreenProps> = () => {
  const { styles, cx } = useMainScreenStyles();
  const { setSteps } = useSteps();

  const { branchData, loading } = useGetBranch();
  const branch = branchData?.branch;

  const weekDays = sortWeekDays(branch?.work_schedules ?? []);

  const onSpecialtyChoose = () => {
    let newSteps = DefaultStepValues.specialtyFirst;
    newSteps[0].status = "process";
    setSteps(newSteps);
  };
  const onScheduleChoose = () => {
    let newSteps = DefaultStepValues.schedulFirst;
    newSteps[0].status = "process";
    setSteps(newSteps);
  };

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      Render
  // ! ||--------------------------------------------------------------------------------

  return (
    <Flex className={styles.wrapper} vertical>
      {branchData?.branch.images[0]?.url && <img src={branchData?.branch.images[0]?.url} className="branchLogo" />}
      <Flex className={styles.body}>
        <Card style={{ border: "none" }} className={styles.content} loading={loading}>
          <Typography.Title className="branchTitle">{branchData?.branch.name}</Typography.Title>
          <Flex gap={8} justify="center">
            {branch?.phone && (
              <a className="infoText" href={`tel:${branch.phone}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M14.665 11.2807V13.2807C14.6657 13.4664 14.6277 13.6502 14.5533 13.8203C14.479 13.9904 14.3699 14.1431 14.233 14.2686C14.0962 14.3941 13.9347 14.4897 13.7588 14.5492C13.5829 14.6087 13.3966 14.6308 13.2117 14.614C11.1602 14.3911 9.18966 13.6901 7.45833 12.5674C5.84755 11.5438 4.48189 10.1782 3.45833 8.56738C2.33165 6.82819 1.63049 4.84805 1.41166 2.78738C1.395 2.60303 1.41691 2.41722 1.47599 2.2418C1.53508 2.06637 1.63004 1.90518 1.75484 1.76846C1.87964 1.63175 2.03153 1.52252 2.20086 1.44773C2.37018 1.37294 2.55322 1.33422 2.73833 1.33405H4.73833C5.06187 1.33086 5.37552 1.44543 5.62084 1.6564C5.86615 1.86737 6.02638 2.16035 6.07166 2.48072C6.15608 3.12076 6.31263 3.7492 6.53833 4.35405C6.62802 4.59266 6.64744 4.85199 6.59427 5.1013C6.5411 5.35061 6.41757 5.57946 6.23833 5.76072L5.39166 6.60738C6.3407 8.27641 7.72263 9.65835 9.39166 10.6074L10.2383 9.76072C10.4196 9.58147 10.6484 9.45795 10.8977 9.40478C11.1471 9.35161 11.4064 9.37102 11.645 9.46072C12.2498 9.68642 12.8783 9.84297 13.5183 9.92738C13.8422 9.97307 14.1379 10.1362 14.3494 10.3857C14.5608 10.6352 14.6731 10.9538 14.665 11.2807Z"
                    stroke="#1677FF"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <Typography.Text>{branch?.phone}</Typography.Text>
              </a>
            )}
            {branch?.address && (
              <Flex gap={8} className="infoText" align="center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3307 6.66732C13.3307 9.99598 9.63806 13.4627 8.39806 14.5333C8.28255 14.6202 8.14193 14.6672 7.9974 14.6672C7.85286 14.6672 7.71225 14.6202 7.59673 14.5333C6.35673 13.4627 2.66406 9.99598 2.66406 6.66732C2.66406 5.25283 3.22597 3.89628 4.22616 2.89608C5.22635 1.89589 6.58291 1.33398 7.9974 1.33398C9.41188 1.33398 10.7684 1.89589 11.7686 2.89608C12.7688 3.89628 13.3307 5.25283 13.3307 6.66732Z"
                    stroke="#1677FF"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 8.66602C9.10457 8.66602 10 7.77059 10 6.66602C10 5.56145 9.10457 4.66602 8 4.66602C6.89543 4.66602 6 5.56145 6 6.66602C6 7.77059 6.89543 8.66602 8 8.66602Z"
                    stroke="#1677FF"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <Typography.Text>
                  {branch?.address.city && `${branch?.address.city}, `}
                  {branch?.address.address_line && `${branch?.address.address_line}`}
                </Typography.Text>
              </Flex>
            )}
          </Flex>
          {branch?.phone && (
            <Flex gap={12} style={{ height: 32 }} className="full-width" justify="center">
              <a href={`tel:${branch.phone}`}>
                <Button className="callButton" type="dashed">
                  Позвонить
                </Button>
              </a>
              <a href={`https://wa.me/${branch.phone}`} target="_blank">
                <Button className="waButton" type="dashed">
                  Написать на WP
                </Button>
              </a>
            </Flex>
          )}

          <Flex vertical className="full-width" gap={8}>
            <Flex align="center" className="chooseBtn" justify="flex-start" gap={8} onClick={onSpecialtyChoose}>
              <div className="specialtyIcon">
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="17" viewBox="0 0 19 17" fill="none">
                  <path
                    d="M14.0833 15.7507C14.0833 13.9825 13.381 12.2868 12.1307 11.0366C10.8805 9.78636 9.18478 9.08398 7.41667 9.08398C5.64856 9.08398 3.95286 9.78636 2.70262 11.0366C1.45238 12.2868 0.75 13.9825 0.75 15.7507"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M7.41667 9.08333C9.71785 9.08333 11.5833 7.21785 11.5833 4.91667C11.5833 2.61548 9.71785 0.75 7.41667 0.75C5.11548 0.75 3.25 2.61548 3.25 4.91667C3.25 7.21785 5.11548 9.08333 7.41667 9.08333Z"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M17.4193 14.9173C17.4193 12.109 15.7526 9.50065 14.0859 8.25065C14.6338 7.83963 15.0719 7.29989 15.3614 6.67921C15.651 6.05853 15.783 5.37604 15.746 4.69215C15.7089 4.00826 15.5038 3.34406 15.1488 2.75832C14.7939 2.17258 14.3 1.68337 13.7109 1.33398"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <Typography.Text>Выбрать специалиста</Typography.Text>
              <div className="arrowRight">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path
                    d="M0.75 12.75L6.75 6.75L0.75 0.75"
                    stroke="#8C8C8C"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>{" "}
              </div>
            </Flex>
            <Flex align="center" className="chooseBtn" justify="flex-start" gap={8} onClick={onScheduleChoose}>
              <div className="specialtyIcon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M6.66406 1.66602V4.99935"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.3359 1.66602V4.99935"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.8333 3.33203H4.16667C3.24619 3.33203 2.5 4.07822 2.5 4.9987V16.6654C2.5 17.5858 3.24619 18.332 4.16667 18.332H15.8333C16.7538 18.332 17.5 17.5858 17.5 16.6654V4.9987C17.5 4.07822 16.7538 3.33203 15.8333 3.33203Z"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M2.5 8.33203H17.5"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66406 11.666H6.6724"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10 11.666H10.0083"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.3359 11.666H13.3443"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66406 15H6.6724"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10 15H10.0083"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.3359 15H13.3443"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <Typography.Text>Выбрать дату и время</Typography.Text>
              <div className="arrowRight">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path
                    d="M0.75 12.75L6.75 6.75L0.75 0.75"
                    stroke="#8C8C8C"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>{" "}
              </div>
            </Flex>
          </Flex>
          <Divider size="small" style={{ margin: 0 }} />
          {branch?.address?.latitude && branch?.address?.longitude && (
            <Flex vertical className="full-width" gap={12}>
              <Typography.Text className="headline text-center">Адрес</Typography.Text>
              <MapboxGlContainer
                height={160}
                latitude={branch?.address?.latitude}
                longitude={branch?.address?.longitude}
              />
            </Flex>
          )}
          <Flex vertical className="full-width" gap={12}>
            <Typography.Text className="headline text-center">Адрес</Typography.Text>
            <Card variant="borderless" className={styles.schedule}>
              <Flex className={styles.weekDays} vertical>
                {weekDays.map((weekDay, index) => (
                  <Row key={weekDay.id}>
                    <Col span={12} style={{ padding: 0 }}>
                      <Typography.Paragraph className={styles.weekdayText}>
                        {WEEKDAY_LABEL[weekDay?.day_of_week !== 7 ? weekDay?.day_of_week : 0]}
                      </Typography.Paragraph>
                    </Col>
                    <Col style={{ padding: 0 }}>
                      <Typography.Paragraph className={styles.weekdayText}>
                        {!weekDay?.is_open ? (
                          <span className={styles.weekend}>{TEXT.schedule.weekend}</span>
                        ) : (
                          `${dayjs(weekDay.time_from, "HH:mm:ss").format("HH:mm")} - ${dayjs(
                            weekDay.time_to,
                            "HH:mm:ss",
                          ).format("HH:mm")}`
                        )}
                      </Typography.Paragraph>
                    </Col>
                    {branch?.work_schedules?.length && index !== branch?.work_schedules?.length - 1 && (
                      <Divider className={styles.weekDayDivider} />
                    )}
                  </Row>
                ))}
              </Flex>
            </Card>
          </Flex>
        </Card>
        <Flex className={cx("full-width", styles.footer)}>
          <Button onClick={onScheduleChoose} type="primary" className="full-width">
            Записаться
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default MainScreen;
